import { CheckIcon, type IconBoxColors, QuestionIcon } from "@hightouch/htui";
import { CreationSource } from "@hightouch/lib/llm/shared";
import pluralize from "pluralize";
import type { AgentDefinitionQuery, AgentDefinitionsQuery } from "src/graphql";
import { formatDateLong, formatFriendlyDay } from "src/utils/time";

export const DEFINITIONS_PATH = "/context-hub/knowledge/definitions";
export const MEMORIES_PATH = "/context-hub/knowledge/memories";

export const definitionPath = (definitionId: string): string =>
  `${DEFINITIONS_PATH}/${definitionId}`;

export type AgentDefinition = AgentDefinitionsQuery["agentDefinitions"][number];
export type AgentDefinitionScope = AgentDefinition["scope"];
type QueriedDefinitionDetail = NonNullable<
  AgentDefinitionQuery["agentDefinition"]
>;
export type AgentDefinitionVersion =
  QueriedDefinitionDetail["versions"][number] & { versionNumber: number };
export type AgentDefinitionDetail = Omit<
  QueriedDefinitionDetail,
  "versions"
> & {
  versions: AgentDefinitionVersion[];
};

// Versions arrive oldest first, so a version's number is its 1-based position.
export const numberVersions = (
  definitionDetail: QueriedDefinitionDetail,
): AgentDefinitionDetail => ({
  ...definitionDetail,
  versions: definitionDetail.versions.map((version, index) => ({
    ...version,
    versionNumber: index + 1,
  })),
});

export const DEFINITION_SEGMENTS = ["all", "needs_review"] as const;
export type DefinitionSegment = (typeof DEFINITION_SEGMENTS)[number];

export const DEFINITION_SORTS = [
  "session_impact",
  "newest_assumed",
  "most_people",
  "name",
] as const;
export type DefinitionSort = (typeof DEFINITION_SORTS)[number];

export const DEFINITION_SORT_LABELS: Record<DefinitionSort, string> = {
  session_impact: "Session impact",
  newest_assumed: "Newest assumed",
  most_people: "Most people",
  name: "A to Z",
};

export const DEFINITION_SCOPE_FILTERS = ["any", "workspace", "scoped"] as const;
export type DefinitionScopeFilter = (typeof DEFINITION_SCOPE_FILTERS)[number];

export const DEFINITION_SCOPE_FILTER_LABELS: Record<
  DefinitionScopeFilter,
  string
> = {
  any: "Any scope",
  workspace: "Workspace",
  scoped: "Scoped",
};

export const DEFINITION_STATUS_LABELS: Record<
  AgentDefinition["status"],
  string
> = {
  assumed: "Assumed",
  verified: "Verified",
};

export const DEFINITION_STATUS_ICONS = {
  assumed: QuestionIcon,
  verified: CheckIcon,
} as const;

const DEFINITION_STATUS_COLORS: Record<
  AgentDefinition["status"],
  IconBoxColors
> = {
  assumed: "yellow",
  verified: "green",
};

export const definitionStatusColor = (
  status: AgentDefinition["status"],
  isMuted = false,
): IconBoxColors => (isMuted ? "gray" : DEFINITION_STATUS_COLORS[status]);

export type ScopeTarget = AgentDefinitionScope["targets"][number];

export const scopeTargetLabel = (target: ScopeTarget): string =>
  target.parentModelName ??
  target.sourceName ??
  (target.parentModelId ? `Model ${target.parentModelId}` : "Source");

export const WORKSPACE_SCOPE_KEY = "workspace";

export type ScopeTargetKey = `model:${string}` | `source:${string}`;
export type ScopeSelectionKey = typeof WORKSPACE_SCOPE_KEY | ScopeTargetKey;

export const modelKey = (id: string): ScopeTargetKey => `model:${id}`;
export const sourceKey = (id: string): ScopeTargetKey => `source:${id}`;

export const scopeTargetKey = (target: ScopeTarget): ScopeTargetKey =>
  target.parentModelId
    ? modelKey(target.parentModelId)
    : sourceKey(target.sourceId ?? "");

export function scopeTargetsLabel(targets: ScopeTarget[]): string {
  const models = targets.filter((target) => target.parentModelId).length;
  const sources = targets.length - models;
  return [
    ...(models > 0 ? [pluralize("model", models, true)] : []),
    ...(sources > 0 ? [pluralize("source", sources, true)] : []),
  ].join(", ");
}

export function isOrphanedScope(scope: AgentDefinitionScope): boolean {
  return scope.scopeMode === "scoped" && scope.targets.length === 0;
}

export function filterDefinitions(
  definitions: AgentDefinition[],
  filters: {
    segment: DefinitionSegment;
    scope: DefinitionScopeFilter;
    search: string;
  },
): AgentDefinition[] {
  const needle = filters.search.trim().toLowerCase();
  return definitions.filter((definition) => {
    if (filters.segment === "needs_review" && definition.status !== "assumed") {
      return false;
    }
    if (
      filters.scope !== "any" &&
      definition.scope.scopeMode !== filters.scope
    ) {
      return false;
    }
    return needle.length === 0 || matchesSearch(definition, needle);
  });
}

function matchesSearch(definition: AgentDefinition, needle: string): boolean {
  return [definition.name, definition.markdown, definition.usageGuidance].some(
    (field) => field?.toLowerCase().includes(needle),
  );
}

const byName = (a: AgentDefinition, b: AgentDefinition) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

const byCreatedDesc = (a: AgentDefinition, b: AgentDefinition) =>
  Date.parse(b.createdAt) - Date.parse(a.createdAt);

export function sortDefinitions(
  definitions: AgentDefinition[],
  sort: DefinitionSort,
): AgentDefinition[] {
  const sorted = [...definitions];
  switch (sort) {
    case "session_impact":
      return sorted.sort(
        (a, b) =>
          b.usage.sessionCount - a.usage.sessionCount ||
          b.usage.personCount - a.usage.personCount ||
          byCreatedDesc(a, b) ||
          byName(a, b),
      );
    case "most_people":
      return sorted.sort(
        (a, b) =>
          b.usage.personCount - a.usage.personCount ||
          b.usage.sessionCount - a.usage.sessionCount ||
          byCreatedDesc(a, b) ||
          byName(a, b),
      );
    case "newest_assumed":
      return sorted.sort(
        (a, b) =>
          Number(b.status === "assumed") - Number(a.status === "assumed") ||
          byCreatedDesc(a, b) ||
          byName(a, b),
      );
    case "name":
      return sorted.sort(byName);
  }
}

export type UsageSummary = { sessions: string | null; detail: string };

export function formatUsageSummary(
  usage: AgentDefinition["usage"],
): UsageSummary {
  if (usage.sessionCount === 0) {
    return { sessions: null, detail: "Not used yet" };
  }
  const parts = [
    usage.personCount === 1 ? "1 person" : `${usage.personCount} people`,
  ];
  if (usage.firstReferencedAt) {
    parts.push(`since ${formatDateLong(usage.firstReferencedAt)}`);
  }
  return {
    sessions: pluralize("session", usage.sessionCount, true),
    detail: parts.join(" · "),
  };
}

// An assumed definition has only ever been written by an agent, so it has no edit to report.
export function revisionTimestamp(definition: AgentDefinition): string {
  return definition.status === "assumed"
    ? definition.createdAt
    : definition.updatedAt;
}

export function formatRevisionLabel(definition: AgentDefinition): string {
  const verb = definition.status === "assumed" ? "Created" : "Updated";
  return `${verb} ${formatFriendlyDay(revisionTimestamp(definition))}`;
}

// Deep links to the specific interaction in a session.
export function sessionInteractionPath(
  creationSource: string | null | undefined,
  sessionId: string,
  interactionId: string,
): string | null {
  return creationSource === CreationSource.Agents ||
    creationSource === CreationSource.Home
    ? `/agents/chats/${sessionId}?${new URLSearchParams({ interactionId })}`
    : null;
}

export const versionLabel = (
  version: Pick<AgentDefinitionVersion, "versionNumber">,
) => `Version ${version.versionNumber}`;

export function dismissedVersionIds(
  versions: AgentDefinitionVersion[],
): Set<string> {
  const firstVerifiedVersion = versions.find(
    (version) => version.status === "verified",
  );
  if (!firstVerifiedVersion?.basedOnVersionId) return new Set();
  return new Set(
    versions
      .filter(
        (version) =>
          version.status === "assumed" &&
          version.versionNumber < firstVerifiedVersion.versionNumber &&
          version.id !== firstVerifiedVersion.basedOnVersionId,
      )
      .map((version) => version.id),
  );
}

export function versionSummary(markdown: string): string {
  const line = markdown
    .split("\n")
    .map((text) => text.replace(/^\s*(?:#+|[-*+]|\d+\.)\s+/, "").trim())
    .find((text) => text.length > 0);
  return line ?? "";
}

export function translateVersionIdsToVersionNumbers(
  versionIds: string[],
  numberByVersionId: ReadonlyMap<string, number>,
): number[] {
  return versionIds
    .flatMap((id) => numberByVersionId.get(id) ?? [])
    .sort((a, b) => a - b);
}

export function formatDefinitionDetailRevisionLabel(
  definitionDetail: Pick<AgentDefinitionDetail, "versions" | "updatedAt">,
): string {
  const verb = definitionDetail.versions.length === 1 ? "Created" : "Updated";
  return `${verb} ${formatFriendlyDay(definitionDetail.updatedAt)}`;
}
